import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Image as ImageIcon } from 'lucide-react';
import { clearPhotoCompressionCache } from '../lib/imageCompress';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRESETS = [
  { label: i18n.t('auto.lbl.webScreen'), px: 1200, q: 70, hint: i18n.t('auto.lbl.smallestFiles') },
  { label: i18n.t('auto.lbl.balanced'), px: 1600, q: 75, hint: 'default' },
  { label: i18n.t('auto.lbl.printA4'), px: 2400, q: 85, hint: i18n.t('auto.lbl.sharperInReports') },
  { label: i18n.t('auto.lbl.archive'), px: 3600, q: 92, hint: 'near-original' },
];

/**
 * Site photo compression limits. Phones shoot 4-12 MB per photo, which
 * stalls the offline queue on a weak signal — these limits decide how
 * much we shrink before upload.
 */
export default function PhotoCompressionSettings({ canEdit = true }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ enabled: true, max_px: 1600, quality: 75 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/settings`);
        setForm({
          enabled: data?.photo_compress_enabled !== false,
          max_px: Number(data?.photo_compress_max_px) || 1600,
          quality: Number(data?.photo_compress_quality) || 75,
        });
      } catch { /* defaults */ }
      setLoading(false);
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/photo-compression`, {
        enabled: form.enabled,
        max_px: Number(form.max_px),
        quality: Number(form.quality),
      });
      clearPhotoCompressionCache();
      toast.success(t('auto.misc2.photoCompressionSaved'));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Card><CardContent className="p-6 text-sm text-slate-500">{t('auto.misc2.loading')}</CardContent></Card>;
  }

  return (
    <Card data-testid="photo-compression-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base md:text-lg flex items-center gap-2">
          <ImageIcon className="h-4 w-4" /> {t('auto.misc2.sitePhotoCompression')}
        </CardTitle>
        <CardDescription className="text-xs sm:text-sm">
          Photos are shrunk on the phone before upload so queues clear on a weak signal.
          Raise the longest edge if photos look soft when printed in reports.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 p-3">
          <div className="min-w-0">
            <p className="text-sm font-medium text-slate-900">{t('auto.misc2.compressBeforeUpload')}</p>
            <p className="text-xs text-slate-500">{t('auto.misc2.turnOffToUploadUntouchedCamera')}</p>
          </div>
          <Switch
            checked={form.enabled}
            onCheckedChange={(v) => setForm((f) => ({ ...f, enabled: v }))}
            disabled={!canEdit}
            data-testid="photo-compress-enabled"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs">{t('auto.misc2.longestEdgePx')}</Label>
            <Input
              type="number"
              min={320}
              max={6000}
              value={form.max_px}
              onChange={(e) => setForm((f) => ({ ...f, max_px: e.target.value }))}
              disabled={!canEdit || !form.enabled}
              data-testid="photo-compress-max-px"
            />
            <p className="text-[11px] text-slate-500 mt-1">
              1600 px prints clean at roughly 13 cm wide; 2400 px fills an A4 column.
            </p>
          </div>
          <div>
            <Label className="text-xs">{t('auto.misc2.jpegQuality30100')}</Label>
            <Input
              type="number"
              min={30}
              max={100}
              value={form.quality}
              onChange={(e) => setForm((f) => ({ ...f, quality: e.target.value }))}
              disabled={!canEdit || !form.enabled}
              data-testid="photo-compress-quality"
            />
            <p className="text-[11px] text-slate-500 mt-1">
              75 is visually lossless for site evidence at a fraction of the size.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {PRESETS.map((p) => (
            <button
              key={p.label}
              type="button"
              onClick={() => setForm((f) => ({ ...f, max_px: p.px, quality: p.q }))}
              disabled={!canEdit || !form.enabled}
              className={`text-xs rounded-full border px-3 py-1.5 transition-colors ${
                Number(form.max_px) === p.px && Number(form.quality) === p.q
                  ? 'border-slate-800 bg-slate-800 text-white'
                  : 'border-slate-300 text-slate-600 hover:bg-slate-100'
              } disabled:opacity-50`}
              data-testid={`photo-compress-preset-${p.px}`}
            >
              {p.label} · {p.px}px / {p.q}% <span className="opacity-70">({p.hint})</span>
            </button>
          ))}
        </div>

        {canEdit && (
          <div className="flex justify-end">
            <Button onClick={save} disabled={saving} data-testid="photo-compress-save">
              {saving ? t('auto.misc2.saving2') : t('auto.misc2.save')}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
